import {
  Category,
  FinanceTotals,
  Transaction,
  TransactionFilters,
  TransactionType,
} from 'types/finance';

export function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function calcTotals(transactions: Transaction[]): FinanceTotals {
  const totalIncome = transactions
    .filter((t) => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalExpense = transactions
    .filter((t) => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  return {
    totalIncome,
    totalExpense,
    balance: totalIncome - totalExpense,
    transactionCount: transactions.length,
  };
}

export function filterTransactions(
  transactions: Transaction[],
  filters: TransactionFilters
): Transaction[] {
  return transactions.filter((tx) => {
    if (filters.type !== 'all' && tx.type !== filters.type) return false;
    if (filters.categoryId && tx.categoryId !== filters.categoryId) return false;

    if (filters.month && filters.month !== 'all') {
      if (!tx.date.startsWith(filters.month)) return false;
    } else {
      if (filters.dateFrom && tx.date < filters.dateFrom) return false;
      if (filters.dateTo && tx.date > filters.dateTo) return false;
    }
    return true;
  });
}

export function currentMonthKey(): string {
  return new Date().toISOString().slice(0, 7);
}

export function monthLabel(monthKey: string): string {
  if (!monthKey || monthKey === 'all') return 'All time';
  const [y, m] = monthKey.split('-');
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const idx = Number(m) - 1;
  return `${names[idx] || m} ${y}`;
}

export function availableMonths(transactions: Transaction[]): string[] {
  const set = new Set(transactions.map((tx) => tx.date.slice(0, 7)).filter(Boolean));
  const current = currentMonthKey();
  set.add(current);
  return Array.from(set).sort().reverse();
}

export function groupByMonth(
  transactions: Transaction[]
): { month: string; income: number; expense: number }[] {
  const map = new Map<string, { income: number; expense: number }>();

  transactions.forEach((tx) => {
    const month = tx.date.slice(0, 7);
    const entry = map.get(month) ?? { income: 0, expense: 0 };
    if (tx.type === 'income') entry.income += tx.amount;
    else entry.expense += tx.amount;
    map.set(month, entry);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({ month, ...data }));
}

export function groupByCategory(
  transactions: Transaction[],
  categories: Category[],
  type: TransactionType
): { name: string; amount: number; color: string }[] {
  const categoryMap = new Map(categories.map((c) => [c.id, c]));
  const totals = new Map<string, number>();

  transactions
    .filter((t) => t.type === type)
    .forEach((tx) => {
      totals.set(tx.categoryId, (totals.get(tx.categoryId) ?? 0) + tx.amount);
    });

  return Array.from(totals.entries()).map(([categoryId, amount]) => {
    const cat = categoryMap.get(categoryId);
    return {
      name: cat?.name ?? 'Unknown',
      amount,
      color: cat?.color ?? '#718096',
    };
  });
}

export function getBalanceOverTime(
  transactions: Transaction[]
): { date: string; balance: number }[] {
  const sorted = [...transactions].sort((a, b) => a.date.localeCompare(b.date));
  let running = 0;
  return sorted.map((tx) => {
    running += tx.type === 'income' ? tx.amount : -tx.amount;
    return { date: tx.date, balance: running };
  });
}

export function groupByDay(
  transactions: Transaction[],
  days = 7
): { label: string; expense: number }[] {
  const result: { label: string; expense: number }[] = [];
  const today = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString('en-US', { weekday: 'short' });
    const expense = transactions
      .filter((t) => t.type === 'expense' && t.date === key)
      .reduce((sum, t) => sum + t.amount, 0);
    result.push({ label, expense });
  }

  return result;
}

export function getCategoryName(
  categories: Category[],
  categoryId: string
): string {
  return categories.find((c) => c.id === categoryId)?.name ?? 'Unknown';
}
